'use client';

import { Icon } from '@/src/components/ui';
import { useToast } from '@/src/components/ui/ToastNotifications';
import { useToggleRepostMutation } from '@/src/hooks/use-toggle-repost-mutation';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { ISocialPost } from 'types/src/types';
import { DotsButton } from './DotsButton';
import { useToggleReactionMutation } from '@/src/hooks/use-toggle-reaction-mutation';
import { useRouter, usePathname } from 'next/navigation';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Modal } from './Modal';
import { ReplyForm } from './comments/ReplyForm';

type Props = {
  isMyPost: boolean;
  post: ISocialPost;
  isAnonymous?: boolean;
  isMobile?: boolean;
  toggleReaction?: { mutate: Function; isPending?: boolean } | null;
  isOpenReplyFromFeed?: ReturnType<typeof useBoolean>;
};

export const PostActions = ({
  isMyPost,
  post,
  isAnonymous,
  isMobile,
  toggleReaction,
  isOpenReplyFromFeed,
}: Props) => {
  const toggleRepost = useToggleRepostMutation();
  const toast = useToast();
  const fallbackToggleReaction = useToggleReactionMutation();
  const pathname = usePathname();
  const isSocialPost = pathname.includes('/social-post/');
  const isControlledFromFeed = !!isOpenReplyFromFeed;
  const reactionMut = toggleReaction ?? fallbackToggleReaction;

  const handleRepostToggle = () => {
    toggleRepost.mutate(
      { postId: post.id, currentlyReposted: !!post.isReposted },
      {
        onSuccess: () => {
          toast.success(post.isReposted ? 'Repost removed' : 'Reposted');
        },
        onError: () => {
          toast.error('Failed to update repost');
        },
      }
    );
  };

  const handleLike = () => {
    reactionMut.mutate(
      { postId: post.id, reaction: 'like' },
      { onError: () => toast.error('Failed to update reaction') }
    );
  };

  const handleDislike = () => {
    reactionMut.mutate(
      { postId: post.id, reaction: 'dislike' },
      { onError: () => toast.error('Failed to update reaction') }
    );
  };

  const score = post.reactionCounts?.score ?? 0;
  const liked = post.myReaction === 'like';
  const disliked = post.myReaction === 'dislike';
  const isOpenReplyModal = useBoolean(false);

  return (
    <div className="flex justify-between md:mt-5 mt-2">
      <Button
        variant="ghost"
        size="sm"
        disabled={isAnonymous && isSocialPost}
        onClick={() => {
          if (isControlledFromFeed) {
            isOpenReplyFromFeed!.onTrue();
          } else {
            isOpenReplyModal.onTrue();
          }
        }}
        className={cn(
          'text-blue-gray hover:bg-transparent hover:text-pink-accent flex items-center ',
          { 'disabled:opacity-100': isSocialPost }
        )}
      >
        <Icon name="post-comment" className="w-4" />
        {post.commentsCount ? post.commentsCount : ''}
      </Button>

      <Button
        variant="ghost"
        size="sm"
        disabled={isAnonymous || toggleRepost.isPending || isMyPost}
        className={cn(
          'text-blue-gray hover:bg-transparent hover:text-pink-accent flex items-center gap-1 text-sm',
          { 'text-pink-accent': post.isReposted }
        )}
        onClick={handleRepostToggle}
      >
        <Icon name="post-repost" className="w-4" />
        {post.repostsCount ? post.repostsCount : ''}
      </Button>

      <Button
        variant="ghost"
        size="sm"
        disabled={isAnonymous}
        onClick={handleLike}
        className={cn(
          'text-blue-gray hover:bg-transparent hover:text-pink-accent',
          { 'text-pink-accent': liked }
        )}
      >
        {score > 0 ? score : ''}
        <Icon name="post-like" className="w-4" />
      </Button>

      <Button
        variant="ghost"
        size="sm"
        disabled={isAnonymous}
        onClick={handleDislike}
        className={cn(
          'text-blue-gray hover:bg-transparent hover:text-pink-accent flex items-center gap-1',
          { 'text-pink-accent': disliked }
        )}
      >
        <Icon name="post-like" className="w-4 rotate-180" />
      </Button>

      {!isMobile && <DotsButton post={post} isMyPost={isMyPost} />}
      {!isControlledFromFeed && (
        <Modal open={isOpenReplyModal.value} isPost={false}>
          <ReplyForm
            handleCloseModal={isOpenReplyModal.onFalse}
            parentCommentId={null}
            replyToComment={null}
            postId={post.id}
          />
        </Modal>
      )}
    </div>
  );
};
